import React, { FunctionComponent, ReactNode, useState } from 'react';
import classnames from 'classnames';

import { CustomFieldKeysResponseObject } from '@api/v4/resources/CustomFieldKeysTypes';
import { AssetsResponse, AssetTypesPascalCaseSingular } from '@api/v4/assets/assetTypes';
import AssetUppy from '@components/common/uppy/asset_uppy';
import { UploadArea } from '@components/common/filestack_uploader/UploadArea';
import { UploadAreaTypes } from '@components/common/filestack_uploader/UploadAreaEnums';
import { handleUploadAssetToSection } from '@components/common/filestack_uploader/helpers';
import oldType from '@helpers/oldType';
import { RequiredCustomFieldsDialog } from '@components/show_page/sections/asset/RequiredCustomFieldsDialog';
import { PrimaryButton } from '@components/library/button';
import { FontIcon, FontIcons } from '@components/library/icon';
import { getHasRequiredCustomFields } from '@components/show_page/sections/asset/required-custom-fields-dialog-helper';
import { CreateNewAssetDialog } from '@components/show_page/sections/asset/CreateNewAssetDialog';
import { CanvaButtonApiV2 } from '@components/show_page/sections/asset/canva/CanvaTypes';
import { CreateNewAssetTypes } from './custom-templates';

import '@components/show_page/sections/asset/styles/asset-uploader.scss';

interface AssetUppyProps {
  activeLabelKey: string;
  canvaApi: CanvaButtonApiV2 | null;
  defaultAssetType: string;
  libraryName: string;
  onAssetProcessing: (numAssets: number) => void;
  onNewAssets: (response: AssetsResponse) => void;
  requiredCustomFieldKeys: Array<CustomFieldKeysResponseObject> | null;
  sectionKey: string;
  showAsGrid: boolean;
}

interface AssetTypeDetails {
  buttonText: string;
  icon?: FontIcons;
  type?: CreateNewAssetTypes;
}

const onNewAssetNonGeneric = (
  activeLabelKey: string,
  defaultAssetType: string,
  sectionKey: string,
): void => {
  let newAssetUrl = `/${BFG.brandfolder_slug}/sections/${sectionKey}/ui/${oldType(defaultAssetType)}/new`;
  const urlParams = {
    ...BFG.resource.type === 'collection' && { collection_slug: BFG.resource.slug }, // eslint-disable-line @typescript-eslint/naming-convention
    ...activeLabelKey && { label_key: activeLabelKey }, // eslint-disable-line @typescript-eslint/naming-convention
  };

  if (Object.keys(urlParams).length > 0) {
    newAssetUrl = `${newAssetUrl}?${new URLSearchParams(urlParams).toString()}`;
  }

  BF.dialog.render(
    `New ${defaultAssetType === 'ExternalMedium' ? 'External Medium' : defaultAssetType}`,
    newAssetUrl,
    BF.handlerGroups.newAsset
  );
};

export const AssetUploader: FunctionComponent<AssetUppyProps> = ({
  activeLabelKey,
  canvaApi,
  defaultAssetType,
  libraryName,
  onAssetProcessing,
  onNewAssets,
  requiredCustomFieldKeys,
  sectionKey
}) => {
  const [showCreateNewAssetDialog, setShowCreateNewAssetDialog] = useState(false);
  const [showRequiredCustomFieldsDialog, setShowRequiredCustomFieldsDialog] = useState(false);

  const canvaEnabled = BFG.hasFeature('canva_button');
  const workspaceEnabled = BFG.hasFeature('workspace') && BFG.resource.is_workspace;
  const customTemplatesEnabled = BFG.hasFeature('custom_templates');
  const canvaOnly = canvaEnabled && !workspaceEnabled && !customTemplatesEnabled;
  const workspaceOnly = workspaceEnabled && !canvaEnabled && !customTemplatesEnabled;
  const featureDetection = canvaEnabled || workspaceEnabled || customTemplatesEnabled;

  const getAssetTypeDetails = (): AssetTypeDetails => {
    if (canvaOnly) {
      return {
        buttonText: bfTranslate('Create from Canva'),
        icon: FontIcons.Palette,
        type: CreateNewAssetTypes.CanvaDesign,
      };
    }

    if (workspaceOnly) {
      return {
        buttonText: bfTranslate('Create task'),
        icon: FontIcons.Task,
        type: CreateNewAssetTypes.Task
      };
    }

    return {
      buttonText: bfTranslate('Create new asset')
    };
  };

  const renderCreateNewAsset = (): ReactNode => {
    const assetTypeDetails = getAssetTypeDetails();
    return (
      <>
        <PrimaryButton
          className="create-new-asset-button"
          icon={assetTypeDetails.icon}
          onClick={(): void => setShowCreateNewAssetDialog(true)}
        >
          {assetTypeDetails.buttonText}
        </PrimaryButton>
        <CreateNewAssetDialog
          canvaApi={canvaApi}
          hideBackButton={canvaOnly || workspaceOnly}
          initialStep={assetTypeDetails.type}
          onAssetProcessing={onAssetProcessing}
          onNewAssets={onNewAssets}
          sectionKey={sectionKey}
          setShowCreateNewAssetDialog={setShowCreateNewAssetDialog}
          showCreateNewAssetDialog={showCreateNewAssetDialog}
        />
      </>
    );
  };

  const hasRequiredCustomFields = getHasRequiredCustomFields(requiredCustomFieldKeys, defaultAssetType);

  let requiredCustomFieldsUploadButton = null;
  if (hasRequiredCustomFields) {
    requiredCustomFieldsUploadButton = (
      <>
        <button
          className="generic-uploader--required-custom-fields"
          onClick={(): void => setShowRequiredCustomFieldsDialog(true)}
          type="button"
        >
          <FontIcon aria-hidden icon={FontIcons.Plus} />
          <p>{bfTranslate('Add assets')}</p>
        </button>
        <RequiredCustomFieldsDialog
          activeLabelKey={activeLabelKey}
          libraryName={libraryName}
          onAssetProcessing={onAssetProcessing}
          onNewAssets={onNewAssets}
          requiredCustomFieldKeys={requiredCustomFieldKeys}
          sectionKey={sectionKey}
          setShowRequiredCustomFieldsDialog={setShowRequiredCustomFieldsDialog}
          showRequiredCustomFieldsDialog={showRequiredCustomFieldsDialog}
        />
      </>
    );
  }

  if (!hasRequiredCustomFields
    && BFG.hasFeature('uppy_uploader_features')
    && defaultAssetType === AssetTypesPascalCaseSingular.GenericFile) {
    return (
      <div className="base-card uppy asset-uploader-component">
        <AssetUppy
          activeLabelKey={activeLabelKey}
          newResources={onNewAssets}
          onAssetProcessing={onAssetProcessing}
          sectionKey={sectionKey}
        />
      </div>
    );
  }

  if (defaultAssetType === AssetTypesPascalCaseSingular.GenericFile) {
    return (
      <div
        className={classnames(
          'base-card filestack-container asset-uploader-component',
          'set-min-height',
          {
            'canva-filestack-container': canvaEnabled && !workspaceEnabled,
            'create-options': workspaceEnabled && canvaEnabled,
            'create-task': workspaceEnabled && !canvaEnabled,
          },
        )}
      >
        {requiredCustomFieldsUploadButton || (
          <UploadArea
            id={sectionKey}
            onUploadDone={(files): void => {
              handleUploadAssetToSection({
                activeLabelKey,
                files,
                onAssetProcessing,
                onNewAssets,
                sectionKey
              });
            }}
            uploadType={UploadAreaTypes.Asset}
          />
        )}
        {featureDetection && renderCreateNewAsset()}
      </div>
    );
  }

  return (
    <div className="base-card generic asset-uploader-component">
      <button
        className="generic-uploader"
        onClick={(): void => onNewAssetNonGeneric(activeLabelKey, defaultAssetType, sectionKey)}
        type="button"
      >
        <span className="bff-plus" />
        <p>{bfTranslate('Add an Asset')}</p>
      </button>
    </div>
  );
};
